import { of } from "rxjs";
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
} from "rxjs/operators";
import {
  Epic,
  combineEpics,
} from "redux-observable";
import {
  AUDIO_CANCEL,
  AUDIO_CHANGE_RATE,
  AUDIO_PAUSE,
  AUDIO_PLAY,
  AUDIO_RESUME,
  AUDIO_SET_VOICE, JUMP_TO_WORD,
  REQUEST_OFFLINE_VOICES,
  SKIP_TEXT_BACKWARD,
  SKIP_TEXT_FORWARD,
} from "./constants";
import {
  AudioActions,
  AudioCancel,
  AudioCancelSuccess,
  AudioChangeRate,
  AudioChangeRateSuccess,
  AudioJumpToWord,
  AudioPauseSuccess,
  AudioPlay,
  AudioResumeSuccess,
  AudioSetVoice,
  AudioSetVoiceSuccess,
  AudioSkipTextForward,
  AudioSkipTextBackward,
  AudioStopSuccess,
  RequestOfflineVoicesSuccess,
} from "./actionTypes";

import {
  audioCancelSuccess,
  audioChangeRateSuccess,
  audioPauseSuccess,
  audioPlay,
  audioResumeSuccess,
  audioSetVoiceSuccess,
  audioStopSuccess,
  requestOfflineVoicesSuccess,
} from "./actions";

import { IRootState } from "../../rootReducer";

import { speech } from "../../../index";

import { DEFAULT_RATE } from "../../../app/constants/speed";

import { getUser } from "../session/selectors";
import { getIsPlaying, getVoices } from "./selectors";

type RequestOfflineVoicesEpic = Epic<AudioActions, RequestOfflineVoicesSuccess, IRootState>;
const requestOfflineVoicesEpic: RequestOfflineVoicesEpic = (action$, state$) => (
  action$.ofType(REQUEST_OFFLINE_VOICES).pipe(
    switchMap(() => (
      state$.pipe(
        map(getUser),
        filter(Boolean),
        distinctUntilChanged(),
      )
    )),
    switchMap(() => speech.getVoices()),
    map(requestOfflineVoicesSuccess),
  )
);

/* TODO: audioStartPlayEpic handles AUDIO_PLAY and dispatch AUDIO_PLAY
*        It creates infinite loop. DO NOT DO THIS
*        https://redux-observable.js.org/docs/basics/Epics.html
*        ******************************************************
*        A: It DOES NOT create an infinite loop. Once the data is empty the loop will exit.
*        This should be viewed from the perspective of recursion.
*        Please remove the comment if the explanation above clarifies the issue.
*/

type AudioStartPlay = Epic<AudioActions, AudioPlay | AudioStopSuccess>;
const audioStartPlayEpic: AudioStartPlay = (action$) => (
  action$.ofType<AudioPlay>(AUDIO_PLAY).pipe(
    switchMap(({ payload }) => speech.speak(payload)),
    map(data => data
      ? audioPlay()
      : audioStopSuccess()
    ),
  )
);

type AudioPausePlay = Epic<AudioActions, AudioPauseSuccess>;
const audioPausePlayEpic: AudioPausePlay = (action$) => (
  action$.ofType(AUDIO_PAUSE).pipe(
    switchMap(() => speech.pause()),
    map(audioPauseSuccess),
  )
);

type AudioResumePlayEpic = Epic<AudioActions, AudioResumeSuccess>;
const audioResumePlayEpic: AudioResumePlayEpic = (action$) => (
  action$.ofType(AUDIO_RESUME).pipe(
    switchMap(() => speech.resume()),
    map(audioResumeSuccess),
  )
);

type AudioSetVoiceEpic = Epic<AudioActions, AudioSetVoiceSuccess, IRootState>;
const audioSetVoiceEpic: AudioSetVoiceEpic = (action$, state$) => (
  action$.ofType<AudioSetVoice>(AUDIO_SET_VOICE).pipe(
    switchMap(({ payload }) => {
      if (payload) {
        return of(payload);
      } else {
        const selectedVoice = localStorage.getItem("selectedVoice");

        return selectedVoice
          ? of(selectedVoice)
          : state$.pipe(
            map(getVoices),
            map(([{ name }]) => name),
          );
      }
    }),
    distinctUntilChanged(),
    switchMap((value) => {
      localStorage.setItem("selectedVoice", value);

      return speech.setVoice(value);
    }),
    map(audioSetVoiceSuccess),
  )
);

type AudioCancelEpic = Epic<AudioActions, AudioCancelSuccess>;
const audioCancelEpic: AudioCancelEpic = (action$) => (
  action$.ofType<AudioCancel>(AUDIO_CANCEL).pipe(
    switchMap(({ payload }) => speech.cancel(payload)),
    map(audioCancelSuccess),
  )
);

type AudioChangeRateEpic = Epic<AudioActions, AudioChangeRateSuccess | AudioStopSuccess | AudioPlay>;
const audioChangeRateEpic: AudioChangeRateEpic = (action$, state$) => (
  action$.ofType<AudioChangeRate>(AUDIO_CHANGE_RATE).pipe(
    map(({ payload }) => {
      if (!payload) {
        const readingRate = localStorage.getItem("readingRate");

        return (readingRate
          ? parseFloat(readingRate)
          : DEFAULT_RATE
        );
      }

      return payload;
    }),
    distinctUntilChanged(),
    tap((rate) => {
      localStorage.setItem("readingRate", rate.toString());
    }),
    switchMap(async (rate) => {
      const isPlaying = getIsPlaying(state$.value);
      const sentenceIndex = speech.sentenceIndex;

      if (isPlaying) {
        await speech.cancel(true);
        speech.recalculateUtterances(sentenceIndex);
      }

      await speech.setRate(rate);

      return ({ rate, isPlaying });
    }),
    mergeMap(({ rate, isPlaying }) => [
      audioChangeRateSuccess(rate),
      !isPlaying  ? audioStopSuccess() : audioPlay()
    ])
  )
);

type audioSkipForwardEpic = Epic<AudioActions, AudioPlay>;
const audioSkipForwardEpic: audioSkipForwardEpic = (action$) => (
  action$.ofType<AudioSkipTextForward>(SKIP_TEXT_FORWARD).pipe(
    switchMap(({ payload }) => speech.skipForward(payload)),
    map(() => audioPlay()),
  )
);

type audioSkipBackwardEpic = Epic<AudioActions, AudioPlay>;
const audioSkipBackwardEpic: audioSkipBackwardEpic = (action$) => (
  action$.ofType<AudioSkipTextBackward>(SKIP_TEXT_BACKWARD).pipe(
    switchMap(({ payload }) => speech.skipBackward(payload)),
    map(() => audioPlay()),
  )
);

type audioJumpToWordEpic = Epic<AudioActions, AudioPlay>;
const audioJumpToWordEpic: audioJumpToWordEpic = (action$) => (
  action$.ofType<AudioJumpToWord>(JUMP_TO_WORD).pipe(
    switchMap(({ payload }) => speech.jumpToWord(payload)),
    map(() => audioPlay()),
  )
);

export default combineEpics(
  audioCancelEpic,
  audioChangeRateEpic,
  audioPausePlayEpic,
  audioResumePlayEpic,
  audioSetVoiceEpic,
  audioStartPlayEpic,
  requestOfflineVoicesEpic,
  audioSkipForwardEpic,
  audioSkipBackwardEpic,
  audioJumpToWordEpic,
);
